import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { motion, AnimatePresence } from "framer-motion";
import { Conversation } from "common/types";
import { defaultColorPallete, handleDownloadFile } from "utils";
import DownloadIcon from "@mui/icons-material/Download";
import { useImageGalleryLogic } from "./useImageGalleryLogic";

export type Imageprops = {
  src: string;
  key: string;
  caption: string | null;
  receivedAt: string;
};

type Props = {
  selectedConversation: Conversation;
  images: Record<string, Imageprops>;
  _selectedImageKey: string | null;
  onClose: () => void;
};

export default function ImageGallery(props: Props) {
  const { selectedConversation, images, _selectedImageKey } = props;
  const {
    handleClose,
    handlePrev,
    toggleZoom,
    scale,
    position,
    selectedImageKey,
    handleNext,
    scrollRef,
    keys,
    isDragging,
    handleImageClick,
    handleMouseUpOrLeave,
    handleMouseDown,
    handleMouseMove,
  } = useImageGalleryLogic(props);

  if (_selectedImageKey === null) return null;

  return (
    <Dialog
      open={_selectedImageKey !== null}
      onClose={handleClose}
      fullScreen={true}
    >
      <DialogContent sx={{ bgcolor: "#0F3542", p: { md: 8 } }}>
        <Box
          key={`OpenedChat-${selectedConversation.id}-senderDetails`}
          display={"flex"}
          alignItems={"center"}
          gap={2}
        >
          <Avatar
            key={`OpenedChat-${selectedConversation.id}-profilePhoto`}
            src={
              selectedConversation?.messages[0]?.contact?.imgData
                ? `data:image/;base64,${selectedConversation?.messages[0]?.contact?.imgData}`
                : undefined
            }
          />
          <Box
            flex={1}
            justifyContent="space-between"
            key={`OpenedChat-${selectedConversation.id}-aditionalInfo`}
          >
            <Typography
              key={`OpenedChat-${selectedConversation.id}-senderName`}
              variant="body2"
              fontWeight="bold"
              color="white"
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {selectedConversation?.messages[0]?.contact?.name ||
                selectedConversation?.name}
            </Typography>
          </Box>
          <Box gap={2} display="flex">
            <IconButton
              onClick={() =>
                handleDownloadFile(images[selectedImageKey]?.src, "image")
              }
              sx={{
                color: "white",
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "white",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box
          pt={4}
          mb={4}
          gap={2}
          sx={{
            height: "75%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <IconButton
            onClick={handlePrev}
            sx={{
              bgcolor: defaultColorPallete.primary.light,
              color: "white",
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <AnimatePresence mode="wait">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={{ sm: "80%", md: "50%" }}
              height="100%"
              onClick={toggleZoom}
              style={{
                overflow: "hidden",
                cursor: scale > 1 ? "zoom-out" : "zoom-in",
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                transition: "transform 0.3s ease",
              }}
            >
              <motion.img
                key={images[selectedImageKey]?.key}
                src={images[selectedImageKey]?.src}
                alt={`Image ${selectedImageKey + 1}`}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                draggable="false"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          </AnimatePresence>
          <IconButton
            onClick={handleNext}
            sx={{
              bgcolor: defaultColorPallete.primary.light,
              color: "white",
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
        <Box
          ref={scrollRef}
          alignItems="center"
          alignContent="center"
          justifyContent="center"
          sx={{
            display: "flex",
            overflowX: "auto",
            overflowY: "hidden",
            p: 1,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            cursor: isDragging.current ? "grabbing" : "grab",
            userSelect: "none",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
        >
          <Box
            sx={{
              minWidth: "40px",
              flexShrink: 0,
            }}
          />
          {keys.map((key) => (
            <Box
              border={
                key === selectedImageKey &&
                `4px solid ${defaultColorPallete.primary.light}`
              }
              onClick={() => handleImageClick(key)}
              key={key}
              component="img"
              draggable="false"
              bgcolor="background.default"
              src={images[key].src}
              alt={images[key].caption ?? `img-${selectedImageKey}`}
              sx={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: 2,
                mr: 1,
                flexShrink: 0,
                pointerEvents: isDragging.current ? "none" : "auto",
              }}
            />
          ))}
          <Box
            sx={{
              minWidth: "50px",
              flexShrink: 0,
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
